import React, { useState, useContext } from 'react';
import { GameDataContext } from '../providers/GameDataProvider';
import { CommunityDragonContext } from '../providers/CommunityDragonProvider';
import { Bar } from 'react-chartjs-2';
import { Team } from '../types/snapshot/Enums';
import { getAggregatedEvents, getWinrateContributions } from '../utils/utils';
export default function WinrateContributionChart() {
    var gameData = useContext(GameDataContext);
    var championSummary = useContext(CommunityDragonContext).championSummary;
    if (!gameData) {
        return null;
    }
    var _a = useState('overall'), selectedCategory = _a[0], setSelectedCategory = _a[1];
    var orderedChampionNames = [gameData.allyRoles.top, gameData.allyRoles.jungle, gameData.allyRoles.mid, gameData.allyRoles.bot, gameData.allyRoles.support];
    var aggEvents = getAggregatedEvents(gameData.events);
    var winrateContributions = getWinrateContributions(aggEvents, gameData.allyRoles, gameData.playerTeam);
    var mvpIndex = orderedChampionNames.reduce(function (acc, championName, i) {
        return winrateContributions[championName].overall > winrateContributions[orderedChampionNames[acc]].overall ? i : acc;
    }, 0);
    var labels = orderedChampionNames.map(function (championName, i) {
        var _a, _b, _c;
        var championData = gameData.snapshots[gameData.snapshots.length - 1].champions.find(function (champion) { return champion.name === championName; });
        var kda = "".concat(championData.kills, " \u00B7 ").concat(championData.deaths, " \u00B7 ").concat(championData.assists);
        var icon;
        if (championSummary.length) {
            var id = (_a = championSummary.find(function (champion) { return champion.alias.toLowerCase() === championName; })) === null || _a === void 0 ? void 0 : _a.id;
            icon = id ? "https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/champion-icons/".concat(id, ".png") : championName;
        }
        else {
            icon = championName;
        }
        var properChampionName = (_c = (_b = championSummary.find(function (champion) { return champion.alias.toLowerCase() === championName; })) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : championName;
        var teamName = i < 5 ? gameData.playerTeam : Team.getOpposite(gameData.playerTeam);
        return { icon: icon, kda: kda, championName: properChampionName, team: teamName, mvp: i === mvpIndex };
    });
    var trueData = orderedChampionNames.map(function (championName) { return winrateContributions[championName][selectedCategory]; });
    var absData = trueData.map(function (value) { return Math.abs(value); });
    var chartData = {
        labels: labels,
        datasets: [{
                data: absData,
                backgroundColor: orderedChampionNames.map(function (championName) {
                    return winrateContributions[championName][selectedCategory] >= 0 ? 'rgba(0, 194, 3, 0.5)' : 'rgba(194, 2, 2, 0.5)';
                }),
                borderColor: orderedChampionNames.map(function (championName, index) {
                    if (selectedCategory === 'overall' && index === mvpIndex) {
                        return 'rgb(218, 165, 32)';
                    }
                    return winrateContributions[championName][selectedCategory] >= 0 ? 'rgba(0, 194, 3, 1)' : 'rgba(194, 2, 2, 1)';
                }),
                borderWidth: 3,
                borderRadius: {
                    topLeft: 5,
                    topRight: 5,
                },
                barThickness: 100,
            }]
    };
    var max = null;
    if (selectedCategory !== 'deaths') {
        var maxPerChampion = orderedChampionNames.map(function (championName) { return Object.entries(winrateContributions[championName]).map(function (_a) {
            var category = _a[0], value = _a[1];
            return category === 'deaths' ? 0 : Math.abs(value);
        }).reduce(function (acc, value) { return Math.max(acc, value); }, 0); });
        max = Math.ceil(Math.max.apply(Math, maxPerChampion) / 5) * 5;
    }
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                bottom: 80, // Reserve space for the custom labels
            }
        },
        scales: {
            x: {
                ticks: {
                    callback: function (value) {
                    },
                },
                border: {
                    display: true,
                    color: 'white',
                    width: 2,
                },
            },
            y: {
                beginAtZero: true,
                min: 0,
                max: max,
                title: {
                    display: true,
                    text: 'Winrate Contribution',
                    color: '#FFFFFF',
                    font: {
                        size: 14,
                    },
                    padding: {
                        bottom: 20,
                    }
                },
                ticks: {
                    color: '#FFFFFF',
                    callback: function (value, index, values) {
                        if (index === values.length - 1) {
                            return '';
                        }
                        return "".concat(value, "%");
                    },
                },
                afterFit: function (scaleInstance) {
                    scaleInstance.width = 80;
                },
                border: {
                    display: true,
                    color: 'white',
                    width: 2,
                },
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: function (tooltipItems) {
                        var item = tooltipItems[0];
                        var label = item.chart.data.labels[item.dataIndex];
                        if (label && label.championName) {
                            return label.championName;
                        }
                        return '';
                    },
                    label: function (context) {
                        var allyTeam = context.dataIndex < 5;
                        var trueValue = trueData[context.dataIndex];
                        var value = allyTeam ? trueValue : -trueValue;
                        return " ".concat(value.toFixed(2), "%");
                    }
                }
            },
            lineChartIcons: {
                enabled: false,
            },
            barChartLabelIcons: {
                enabled: true,
            },
            barChartMiniLabelIcons: {
                enabled: false,
            },
            crosshair: false,
            zoom: false,
        }
    };
    var getButtonStyle = function (category) {
        var buttonStyle = {
            background: category === selectedCategory ? 'white' : 'transparent',
            fontFamily: 'Lora',
            color: category === selectedCategory ? 'rgb(2, 0, 31)' : 'white',
            height: 30,
            width: 120,
            padding: '4px 10px',
            border: '1px solid white',
            borderRadius: 4,
            marginLeft: 15,
            outline: 'none',
            cursor: 'pointer',
        };
        return buttonStyle;
    };
    if (Object.keys(winrateContributions).length === 0) {
        return null;
    }
    return (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', width: '100%', height: '100%' } },
        React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: 10 } },
            React.createElement("button", { style: getButtonStyle('overall'), onClick: function () { return setSelectedCategory('overall'); } }, "Overall"),
            React.createElement("button", { style: getButtonStyle('gameImpact'), onClick: function () { return setSelectedCategory('gameImpact'); } }, "Impact"),
            React.createElement("button", { style: getButtonStyle('deaths'), onClick: function () { return setSelectedCategory('deaths'); } }, "Deaths")),
        React.createElement(Bar, { data: chartData, options: options })));
}
